/**
 * Standorte, Positionen und Qualifikationen.
 *
 * Sitzt auf der Betriebsseite, weil es dorthin gehört: Wer Standorte pflegt,
 * pflegt gerade den Betrieb, nicht den Plan.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  api,
  ApiError,
  type ListResponse,
  type Location,
  type Member,
  type Position,
} from '../lib/api'
import { Banner, Button, Card, Chip, Dialog, TextField, useToast } from '../ui'

export function MasterData({ tenantId }: { tenantId: string }) {
  return (
    <>
      <SimpleList
        kind="locations"
        tenantId={tenantId}
        title="Standorte"
        hint="Filiale, Bereich oder Abteilung — Küche, Gastraum, Biergarten."
        defaultColor="#4F5B92"
        withAbbreviation={false}
      />
      <SimpleList
        kind="positions"
        tenantId={tenantId}
        title="Positionen"
        hint="Funktion in der Schicht — Koch, Service, Tresen. Nicht zu verwechseln mit der Rolle im System."
        defaultColor="#585E71"
        withAbbreviation
      />
      <Qualifications tenantId={tenantId} />
    </>
  )
}

type Entry = Location & Partial<Position>

function SimpleList({
  kind,
  tenantId,
  title,
  hint,
  defaultColor,
  withAbbreviation,
}: {
  kind: 'locations' | 'positions'
  tenantId: string
  title: string
  hint: string
  defaultColor: string
  withAbbreviation: boolean
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [abbreviation, setAbbreviation] = useState('')
  const [color, setColor] = useState(defaultColor)
  const [error, setError] = useState<string | null>(null)

  const list = useQuery({
    queryKey: [kind, tenantId],
    queryFn: () => api.get<ListResponse<Entry>>(`/api/${kind}`),
  })

  const create = useMutation({
    mutationFn: () =>
      api.post(`/api/${kind}`, {
        name,
        color,
        ...(withAbbreviation ? { abbreviation: abbreviation || name.slice(0, 3).toUpperCase() } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [kind] })
      toast(`${title.slice(0, -1)} angelegt.`, 'success')
      setName('')
      setAbbreviation('')
      setError(null)
      setOpen(false)
    },
    onError: (err) => setError((err as ApiError).message),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/${kind}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [kind] })
      toast('Entfernt.', 'success')
    },
    onError: (err) => toast((err as ApiError).message, 'error'),
  })

  return (
    <Card
      title={title}
      action={
        <Button small onClick={() => setOpen(true)}>
          Hinzufügen
        </Button>
      }
    >
      <div className="stack stack--tight">
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          {hint}
        </p>
        {list.data?.items.length === 0 && <p className="muted">Noch nichts angelegt.</p>}
        {list.data?.items.map((entry) => (
          <div key={entry.id} className="row row--between">
            <span className="row">
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: entry.color,
                  display: 'inline-block',
                }}
              />
              <span>{entry.name}</span>
              {entry.abbreviation && <Chip>{entry.abbreviation}</Chip>}
            </span>
            <Button variant="text" small onClick={() => remove.mutate(entry.id)}>
              Entfernen
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} title={`${title.slice(0, -1)} hinzufügen`}>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            create.mutate()
          }}
        >
          {error && <Banner tone="error">{error}</Banner>}
          <TextField
            label="Name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {withAbbreviation && (
            <TextField
              label="Kürzel"
              maxLength={8}
              value={abbreviation}
              onChange={(event) => setAbbreviation(event.target.value)}
              hint="Für die Anzeige im engen Wochenraster. Leer = aus dem Namen abgeleitet."
            />
          )}
          <div className="field">
            <span className="field__label">Farbe</span>
            <input
              type="color"
              value={color}
              onChange={(event) => setColor(event.target.value)}
              style={{
                width: 56,
                height: 44,
                border: 'none',
                borderRadius: 'var(--md-sys-shape-md)',
                background: 'transparent',
                cursor: 'pointer',
              }}
            />
          </div>
          <div className="row row--between">
            <Button variant="text" type="button" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" busy={create.isPending} disabled={name.trim().length < 1}>
              Anlegen
            </Button>
          </div>
        </form>
      </Dialog>
    </Card>
  )
}

// ------------------------------------------------------------ Qualifikation

function Qualifications({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const members = useQuery({
    queryKey: ['members', tenantId],
    queryFn: () => api.get<ListResponse<Member>>('/api/members'),
  })
  const positions = useQuery({
    queryKey: ['positions', tenantId],
    queryFn: () => api.get<ListResponse<Position>>('/api/positions'),
  })

  return (
    <Card title="Qualifikationen">
      <div className="stack">
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          Wer welche Position besetzen darf. Fehlt ein Häkchen, warnt die Planung — sie sperrt
          aber nicht: Wer eingearbeitet wird, steht im Plan, bevor das Häkchen sitzt.
        </p>
        {positions.data?.items.length === 0 && (
          <Banner tone="info">Lege zuerst Positionen an.</Banner>
        )}
        {members.data?.items.map((member) => (
          <MemberQualifications
            key={member.membership_id}
            member={member}
            positions={positions.data?.items ?? []}
            onSaved={() => {
              toast('Qualifikationen gespeichert.', 'success')
              queryClient.invalidateQueries({ queryKey: ['qualifications'] })
            }}
          />
        ))}
      </div>
    </Card>
  )
}

function MemberQualifications({
  member,
  positions,
  onSaved,
}: {
  member: Member
  positions: Position[]
  onSaved: () => void
}) {
  const current = useQuery({
    queryKey: ['qualifications', member.membership_id],
    queryFn: () =>
      api.get<ListResponse<Position>>(`/api/members/${member.membership_id}/positions`),
  })

  const save = useMutation({
    mutationFn: (ids: string[]) =>
      api.put(`/api/members/${member.membership_id}/positions`, { position_ids: ids }),
    onSuccess: () => {
      current.refetch()
      onSaved()
    },
  })

  const selected = new Set((current.data?.items ?? []).map((p) => p.id))

  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    save.mutate([...next])
  }

  return (
    <div className="row row--between">
      <span>{member.full_name}</span>
      <span className="row">
        {positions.map((position) => (
          <label key={position.id} className="row" style={{ gap: 'var(--sp-1)' }}>
            <input
              type="checkbox"
              checked={selected.has(position.id)}
              disabled={save.isPending || current.isPending}
              onChange={() => toggle(position.id)}
              style={{ width: 'auto', flex: '0 0 auto' }}
            />
            <span style={{ fontSize: '0.8125rem' }}>{position.abbreviation}</span>
          </label>
        ))}
      </span>
    </div>
  )
}
